<template>
  <div
    ref="container"
    class="tooltip-container"
    @mouseenter="hovered = true"
    @mouseleave.stop="hovered = false"
  >
    <slot></slot>
    <Teleport :to="teleportTo">
      <div
        v-if="!disabledTooltip || (isTextTooLong && heatmap)"
        ref="tooltip"
        class="tooltip"
        :style="tooltipStyle"
      >
        <span class="text">{{ text }}</span>
      </div>
      <Arrow
        v-if="!disabledTooltip || (isTextTooLong && heatmap)"
        class="arrow"
        :style="arrowStyle"
      />
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref, computed, watch } from 'vue'
import Arrow from '@/design-system/atoms/TooltipArrow.vue'

type BoundingRect = {
  x: number
  y: number
  width: number
  height: number
  top: number
  right: number
  bottom: number
  left: number
}
type Positioning =
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'top-left'
  | 'top-right'
  | 'center-left'
  | 'center-right'

const props = defineProps({
  text: {
    type: String,
    required: false
  },
  teleportTo: {
    type: String,
    required: false,
    default: '#app'
  },
  arrowPosition: {
    type: String as PropType<Positioning>,
    required: false,
    default: 'bottom-right'
  },
  horizontalOffset: {
    type: Number,
    required: false,
    default: 0
  },
  verticalOffset: {
    type: Number,
    required: false,
    default: 0
  },
  tooltipOffset: {
    type: Number,
    required: false,
    default: 0
  },
  heatmap: {
    type: Boolean,
    required: false,
    default: false
  },
  disabledTooltip: {
    type: Boolean,
    required: false,
    default: false
  }
})

const container = ref<HTMLElement | undefined>()
const tooltip = ref<HTMLElement | undefined>()
const hovered = ref(false)
const tooltipStyle = ref<any>({})
const arrowStyle = ref<any>({})
const arrowHeight = 10
const arrowWidth = 20

const opacity = computed(() => {
  return hovered.value ? 1 : 0
})
const isTextTooLong = computed(() => {
  // Define a threshold for the maximum length of text that should trigger the tooltip
  const maxTextLength = 17 // You can change this value to your desired length

  return props.text && props.text.length > maxTextLength
})

watch(hovered, (isHovered, oldValue) => {
  if (isHovered != oldValue && props.text) {
    if (isHovered) {
      showTooltip()
    } else {
      hideTooltip()
    }
  }
})

function hideTooltip() {
  tooltipStyle.value = {
    visibility: 'hidden',
    opacity: 0
  }
  arrowStyle.value = {
    visibility: 'hidden',
    opacity: 0
  }
}

function showTooltip() {
  if (!container.value || !tooltip.value) {
    return
  }
  const containerRect: BoundingRect = container.value.getBoundingClientRect()
  const tooltipRect: BoundingRect = tooltip.value.getBoundingClientRect()
  arrowStyle.value = {
    visibility: 'visible',
    opacity: 1,
    ...makeArrowStyle(containerRect)
  }
  tooltipStyle.value = {
    visibility: 'visible',
    opacity: 1,
    ...makeTooltipStyle(containerRect, tooltipRect)
  }
}

function makeArrowStyle(containerRect: BoundingRect) {
  switch (props.arrowPosition) {
    case 'top-left':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + props.horizontalOffset}px`,
        'transform-origin': 'top left',
        transform: `translateY(${arrowHeight}px) translateX(${arrowWidth}px) rotate(180deg)`
      }
    case 'top-center':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + props.horizontalOffset}px`,
        'transform-origin': 'top left',
        transform: `translateY(${arrowHeight}px) translateX(${
          arrowWidth + containerRect.width / 2 - arrowWidth / 2
        }px) rotate(180deg)`
      }
    case 'top-right':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width * 1.45 - props.horizontalOffset}px`,
        'transform-origin': 'top left',
        transform: `translateY(${arrowHeight}px) translateX(-${arrowWidth}px) rotate(180deg)`
      }
    case 'bottom-left':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left - props.horizontalOffset}px`,
        transform: `translateY(-${arrowHeight}px)`
      }
    case 'bottom-center':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + props.horizontalOffset}px`,
        transform: `translateY(-${arrowHeight}px) translateX(${
          containerRect.width / 2 - arrowWidth / 2
        }px)`
      }
    case 'bottom-right':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width - props.horizontalOffset}px`,
        transform: `translateY(-${arrowHeight}px) translateX(-${arrowWidth}px)`
      }
    case 'center-left':
      return {
        top: `${containerRect.top + props.verticalOffset + containerRect.height}px`,
        left: containerRect.left + props.horizontalOffset,
        'transform-origin': 'top left',
        transform: `translateY(-${
          containerRect.height / 2 - arrowWidth / 2
        }px) translateX(-${arrowHeight}px) rotate(270deg)`
      }
    case 'center-right':
      return {
        top: `${containerRect.top + props.verticalOffset + containerRect.height}px`,
        left: containerRect.left + containerRect.width + props.horizontalOffset,
        'transform-origin': 'top left',
        transform: `translateY(-${arrowWidth / 2 + containerRect.height / 2}px) translateX(${
          arrowWidth / 2
        }px) rotate(90deg)`
      }
    default:
      throw new Error('Fatal: Unsupported arrow position')
  }
}

function makeTooltipStyle(containerRect: BoundingRect, tooltipRect: BoundingRect) {
  switch (props.arrowPosition) {
    case 'bottom-left':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left - 6}px`,
        transform: `translateY(-${tooltipRect.height + arrowHeight}px) translateX(-${
          props.tooltipOffset
        }px)`
      }
    case 'bottom-center':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width / 2}px`,
        transform: `translateY(-${tooltipRect.height + arrowHeight}px) translateX(-50%)`
      }
    case 'bottom-right':
      return {
        top: `${containerRect.top - props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width + 6}px`,
        transform: `translateY(-${tooltipRect.height + arrowHeight}px) translateX(${
          props.tooltipOffset - tooltipRect.width + props.horizontalOffset
        }px)`
      }
    case 'top-left':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left}px`,
        transform: `translateY(${arrowHeight}px) translateX(${props.tooltipOffset}px)`
      }
    case 'top-center':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width + props.horizontalOffset}px`,
        transform: `translateY(${arrowHeight}px) translateX(-${
          tooltipRect.width / 2 + containerRect.width / 2 + props.tooltipOffset
        }px)`
      }
    case 'top-right':
      return {
        top: `${containerRect.top + containerRect.height + props.verticalOffset}px`,
        left: `${containerRect.left + containerRect.width * 1.2}px`,
        transform: `translateY(${arrowHeight}px) translateX(-${
          tooltipRect.width + props.tooltipOffset - props.horizontalOffset
        }px)`
      }
    case 'center-right':
      return {
        top: `${containerRect.top + (props.verticalOffset + containerRect.height)}px`,
        left: `${
          containerRect.left + (containerRect.width + arrowHeight + props.horizontalOffset)
        }px`,
        transform: `translateY(-${tooltipRect.height / 2 + containerRect.height / 2}px)`
      }
    case 'center-left':
      return {
        top: `${containerRect.top + props.verticalOffset}px`,
        left: `${
          containerRect.left - (tooltipRect.width + arrowHeight + props.horizontalOffset)
        }px`,
        transform: `translateY(-${tooltipRect.height / 2 - containerRect.height / 2}px)`
      }
    default:
      throw new Error('Fatal: Unsupported arrow position')
  }
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/variables.scss';
.tooltip-container {
  display: inherit;
  position: relative;
  overflow: inherit;
}
.tooltip {
  pointer-events: none;
  border-radius: variables.$border-radius-small;
  padding: variables.$padding-large variables.$padding-xlarge;
  background-color: variables.$color-greyscale-content;
  color: variables.$color-pure-white;
  display: flex;
  justify-content: center;
  left: 0;
  max-width: variables.$tooltip-width;
  min-width: variables.$tooltip-min-width;
  white-space: pre-line;
  overflow-wrap: anywhere;
  top: 0;
  width: max-content;
  opacity: v-bind(opacity);
  transition: 'opacity' 1s;
  position: absolute;
  visibility: 'hidden';
  z-index: variables.$z-index-tooltip;
}
.text {
  @include font-mixins.desktop-regular-label;
}
.arrow {
  color: variables.$color-greyscale-content;
  height: 10px;
  width: 20px;
  justify-content: end;
  left: 0;
  opacity: v-bind(opacity);
  top: 0;
  transition: 'opacity' 1s;
  position: absolute;
  visibility: 'hidden';
  z-index: variables.$z-index-tooltip;
}
</style>
